"use client";

import { useRouter } from "next/navigation";
import { Sparkles, ArrowRight } from "lucide-react";
import { INSIGHTS_PER_PERSONA, type Insight } from "@petci/shared-ui";
import { useActivePersona } from "@/lib/persona";
import { useT, useLocaleTag } from "@/lib/locale";
import { formatDate } from "@/lib/format";

const VISIBLE_LIMIT = 3;

export function PelicanInsightsCard() {
  const persona = useActivePersona();
  const router = useRouter();
  const insights = INSIGHTS_PER_PERSONA[persona.id];
  const t = useT();
  const tag = useLocaleTag();
  // W6 closes the H2 deferral: the insight body/cta prose is now trilingual via
  // additive `bodyKey`/`ctaKey` in shared-ui. `body`/`cta` stay the English SSoT
  // (mobile / byte-identical fallback); web resolves the keys here. The two
  // dynamic bodies interpolate a locale-formatted date (D12) or a keyed recency
  // phrase — computed below since shared-ui holds no active locale.

  if (insights.length === 0) return null;

  // Resolve an insight's body: prefer the i18n key (W6), substituting the {date}
  // param locale-correctly (D12) and the {recency} param from its own key; fall
  // back to the English `body` SSoT for any not-yet-keyed insight.
  function resolveBody(insight: Insight): string {
    if (!insight.bodyKey) return insight.body;
    const params: Record<string, string> = {};
    if (insight.bodyParams?.date !== undefined) {
      params.date = formatDate(insight.bodyParams.date, tag);
    }
    if (insight.bodyParams?.recencyKey) {
      params.recency = t(insight.bodyParams.recencyKey);
    }
    return t(insight.bodyKey, Object.keys(params).length > 0 ? params : undefined);
  }

  // R-D: the two lead figures carrying unit vocabulary ("2 hr 40 min",
  // "32 kWh/day") resolve through a key; bare-numeral figures keep the English
  // SSoT field, which is locale-neutral. Mobile stays on `leadFigure` either way.
  function resolveLeadFigure(insight: Insight): string {
    if (!insight.leadFigureKey) return insight.leadFigure;
    return t(insight.leadFigureKey, insight.leadFigureParams);
  }

  function resolveCta(insight: Insight): string | null {
    if (insight.cta === null) return null;
    return insight.ctaKey ? t(insight.ctaKey) : insight.cta;
  }

  function handleAction(insight: Insight) {
    if (insight.action.kind === "assistant") {
      // Carry intentId (D4) so the assistant resolves the canned fallback answer
      // by id — a reworded prompt can no longer dead-end on the filler answer.
      const { prompt, intentId } = insight.action;
      router.push(
        `/support?prompt=${encodeURIComponent(prompt)}&intentId=${encodeURIComponent(intentId)}`,
      );
    } else {
      router.push(insight.action.webPath);
    }
  }

  return (
    <div className="card-surface space-y-3">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center">
          <Sparkles size={16} className="text-brand-primary-text" aria-hidden />
        </span>
        <h2 className="text-sm font-semibold text-neutral-700">{t("home.pelicanInsights")}</h2>
      </div>

      <ul className="space-y-1" role="list">
        {insights.slice(0, VISIBLE_LIMIT).map((insight) => {
          const cta = resolveCta(insight);
          return (
            <li key={insight.id}>
              <button
                type="button"
                onClick={() => handleAction(insight)}
                className="w-full text-left flex items-start gap-3 rounded-lg p-3 hover:bg-brand-primary/5 transition-colors duration-fast group"
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="text-sm text-neutral-700 leading-snug">
                    <span className="font-display font-bold text-brand-primary-text text-base">
                      {resolveLeadFigure(insight)}
                    </span>{" "}
                    {resolveBody(insight)}
                  </p>
                  {cta && (
                    <p className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
                      {cta}
                      <ArrowRight size={11} aria-hidden />
                    </p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
